import React, { useEffect, useState } from "react";
import Loader from "react-loader-spinner";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip,XAxis, YAxis } from "recharts";
import { Legends } from "./ConcessionaireDashboardChart";
import { numberWithCommas } from "../../service/utilService";


function TrendBarChart({ data, loader, entryPoint }) {
  const [chartData, setChartData] = useState([]);
  const [uniqLaneId, setUniqLaneId] = useState([]);
  const [singleMode, setSingleMode] = useState(false);

  useEffect(() => {
    if (entryPoint) modifyEntryChartData(data);
    else modifyChartData(data);
  }, [data]);

  const modifyChartData = async (data) => {
    let finalChartData = [];
    //for Multiple plaza
    if ([...new Set([...data.map((e) => e.laneId)])].length > 1) {
      setSingleMode(false);
      finalChartData = data.reduce((a, v) => {
        const { laneId, avc, cases } = v;
        if (a[laneId]) {
          a[laneId] = {
            ...a[laneId],
            [avc]: (+a[laneId][avc] || 0) + +cases,
            total: a[laneId].total + +cases,
          };
        } else {
          a[laneId] = { name: laneId, [avc]: +cases, total: +cases };
        }
        return a;
      }, {});
    } else {
      //for single plaza
      setSingleMode(true);
      finalChartData = data.reduce((a, v) => {
        const { laneId, avc, cases } = v;
        a.push({ name: avc, laneId, [avc]: cases, total: cases });
        return a;
      }, []);
    }
    const final = Object.values(finalChartData);
    final.sort((a, b) => b.total - a.total);
    if (final.length > 10) final.length = 10;
    final.sort((a, b) => (a.name?.replace(/[a-zA-Z ]/g,"") || 0) - (b.name?.replace(/[a-zA-Z ]/g,"") || 0));
    setChartData(final);
    setUniqLaneId([...new Set([...data.map((e) => e.avc)])]);
  };

  // Function for Entry Plaza
  const modifyEntryChartData = async (data) => {
    let finalChartData = [];
    //for Multiple plaza
    if ([...new Set([...data.map((e) => e.entry_toll_plaza_id)])].length > 1) {
      setSingleMode(false);
      finalChartData = data.reduce((a, v) => {
        const { entry_toll_plaza_id: plazaId, avc, cases, sumAmount } = v;
        if (a[plazaId]) {
          a[plazaId] = {
            ...a[plazaId],
            [avc]: (+a[plazaId][avc] || 0) + +cases,
            [avc + "amount"]: (+a[plazaId][avc + "amount"] || 0) + +sumAmount,
            total: a[plazaId].total + +cases,
          };
        } else {
          a[plazaId] = {
            name: plazaId,
            [avc]: +cases,
            total: +cases,
            [avc + "amount"]: +sumAmount,
          };
        }
        return a;
      }, {});
    } else {
      //for single plaza
      setSingleMode(true);
      finalChartData = data.reduce((a, v) => {
        const { entry_toll_plaza_id: plazaId, avc, cases, sumAmount } = v;
        a.push({
          name: avc,
          plazaId,
          [avc]: cases,
          total: cases,
          [avc + "amount"]: +sumAmount,
        });
        return a;
      }, []);
    }
    const final = Object.values(finalChartData);
    final.sort((a, b) => b.total - a.total);
    if (final.length > 10) final.length = 10;
    setChartData(final);
    setUniqLaneId([...new Set([...data.map((e) => e.avc)])].sort());
  };

  const CustomTooltip = ({ active, payload, datakey }) => {
    if (active && payload) {
      const total = payload[0]?.payload.total;
      const plazaName = payload[0]?.payload.name;
      const plazaId = payload[0]?.payload.plazaId;
      return (
        <div className="line-chart_custom-tooltip">
          <p>Lane ID : {singleMode ? plazaId : plazaName}</p>
          {payload
            .sort(
              (a, b) =>
                a?.payload[a.name + "amount"] - b?.payload[b.name + "amount"]
            )
            .map((_, i, arr) => {
              const index = arr.length - (i + 1);
              const data = arr[index];
              const amount = data?.payload[data.name + "amount"];
              return (
                <p className="label">
                  <span
                    className="color-dot"
                    style={{ backgroundColor: data.color }}
                  ></span>{" "}
                  &nbsp; {data.name} : {data.value} &nbsp; (
                  {entryPoint ? (
                    <>&#8377;{numberWithCommas(amount, 2)}</>
                  ) : (
                    ((data.value / total) * 100).toFixed(2) + "%"
                  )}
                  )
                </p>
              );
            })}
        </div>
      );
    }
    return null;
  };

  const entryChartCOLOR = [
    "#FDA629",
    "#AA4548",
    "#DB3236",
    "#FFE038",
    "#B5B5B5",
    "#dc3545",
    "#198754",
    "#0dcaf0",
    "#6c757d",
    "aqua",
    "blue",
    "fuchsia",
    "gray",
    "green",
    "lime",
    "maroon",
    "navy",
    "olive",
    "orange",
    "purple",
    "red",
    "silver",
    "teal",
    "white",
    "yellow",
  ];

  const piChartColor = [
    "#f57371",
    "#f57371",
    "#f59671",
    "#f5b371",
    "#f5e371",
    "#cdf571",
    "#92f571",
    "#71f5a6",
    "#71f1f5",
    "#71f1f5",
    "#71c0f5",
    "#71c0f5",
    "#719ff5",
    "#719ff5",
    "#7178f5",
    "#7178f5",
    "#8e71f5",
    "#8e71f5",
    "#a471f5",
    "#cd71f5",
    "#cd71f5",
    "#f571f1",
    "#f571a4",
    "#f5717c",
    "#ed602d",
    "#ed602d",
    "#ed9a2d",
    "#ed9a2d",
    "#edda2d",
    "#edda2d",
    "#c7ed2d",
    "#6aed2d",
    "#6aed2d",
    "#2deda0",
    "#2deda0",
    "#2dd7ed",
    "#2dd7ed",
    "#2dd7ed",
    "#2da3ed",
    "#2da3ed",
    "#2d5aed",
    "#2d5aed",
    "#732ded",
    "#732ded",
    "#bd2ded",
    "#bd2ded",
    "#ed2dd7",
    "#ed2dd7",
    "#ed2d9a",
    "#ed2d6a",
    "#733808",
    "#733808",
    "#6e7308",
    "#6e7308",
    "#6e7308",
  ];

  const colorArr = entryPoint ? entryChartCOLOR : entryChartCOLOR;

  function formatYAxis(value) {
    return numDifferentiation(value);
  }

  const numDifferentiation = (value) => {
    var val = Math.abs(value);
    if (val >= 10000000) {
      val = (val / 10000000).toFixed(2) + " Cr";
    } else if (val >= 100000) {
      val = (val / 100000).toFixed(2) + " Lac";
    } else if (val >= 1000) {
      val = (val / 1000).toFixed(2) + "K";
    }
    return val;
  };

  return (
    <div className="trend-bar-chart-wrapper">
      {loader ? (
        <Loader
          className="loader chart-loader"
          type="ThreeDots"
          color="#9c1d26"
          width={100}
          visible={loader}
        />
      ) : (
        <>
          {data.length > 0 ? (
            <ResponsiveContainer height={400} width="100%">
              <BarChart
                data={chartData}
                barSize={74}
                margin={{ top: 40, left: 0, right: 0, bottom: 0 }}
              >
                <CartesianGrid stroke="#CCCCCC4F" strokeDasharray="2" />
                <XAxis
                  dataKey="name"
                  label={{
                    value: singleMode ? "Lane Ids" : "Lane Ids",
                    position: "insideBottom",
                    stroke: "#B2ACAC",
                  }}
                  height={50}
                  style={{ fontSize: 12 }}
                />
                <YAxis
                  tickFormatter={formatYAxis}
                  label={
                    entryPoint
                      ? {
                          value: "Count",
                          angle: -90,
                          position: "insideLeft",
                          dy: 30,
                          dx: 3,
                          stroke: "#B2ACAC",
                        }
                      : {
                          value: "Lanewise Txn Count",
                          angle: -90,
                          position: "insideLeft",
                          dy: 110,
                          stroke: "#B2ACAC",
                        }
                  }
                  width={80}
                  style={{ fontSize: 12 }}
                />
                <Tooltip
                  cursor={{ fill: "transparent" }}
                  content={<CustomTooltip />}
                />
                {uniqLaneId.map((laneId, i) => (
                  <Bar
                    dataKey={String(laneId)}
                    stackId="a"
                    fill={
                      i < 49
                        ? colorArr[i]
                        : `#${Math.floor(Math.random() * 9715).toString(16)}`
                    }
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="no-record-found">Record Not Found</p>
          )}
        </>
      )}
    </div>
  );
}

export default React.memo(TrendBarChart)
